/**
 * Service-worker registration, and the update signal.
 *
 * Registered from application code rather than an injected inline script,
 * because the deployed CSP is `script-src 'self'` and an inline registration
 * would be blocked in exactly the build that needs it.
 *
 * The update is OFFERED, never applied. Every artifact this app produces is
 * stamped with the build that made it, so a worker that swapped itself in
 * silently could leave one analyst reading a report stamped with a build they
 * are no longer running, and two analysts on different builds with nothing on
 * screen to say so.
 */

export type UpdateHandle = {
  /** Apply the waiting version and reload. */
  update: () => void;
};

type Listener = (handle: UpdateHandle) => void;

let pending: UpdateHandle | null = null;
const listeners = new Set<Listener>();

/** Called by the app once, at start-up. Safe to call where no worker exists. */
export async function registerServiceWorker(): Promise<void> {
  // Dynamic, so a build without the plugin (and the test environment) does not
  // fail to resolve the virtual module at import time.
  const { registerSW } = await import("virtual:pwa-register");
  const update = registerSW({
    onNeedRefresh() {
      announce({ update: () => update(true) });
    }
  });
}

function announce(handle: UpdateHandle): void {
  pending = handle;
  for (const listener of listeners) listener(handle);
}

/** Subscribe to "a new version is waiting". Returns an unsubscribe function. */
export function onUpdateAvailable(listener: Listener): () => void {
  listeners.add(listener);
  // A subscriber that mounts after the worker reported the update still hears
  // about it; otherwise the toast depends on render order.
  if (pending !== null) listener(pending);
  return () => listeners.delete(listener);
}

/** Test seam: publish an update as though the worker had reported one. */
export function __announceUpdateForTests(handle: UpdateHandle): void {
  announce(handle);
}

/** Test seam: forget any pending update between tests. */
export function __resetUpdateStateForTests(): void {
  pending = null;
  listeners.clear();
}
